import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import { createCategory, updateCategory, deleteProductCategory, getAllCategories, uploadImageToMedia, getAllOrderData, getAllProductsByPage, getAllCustomers } from '../Api';
import { BsGrid3X3GapFill } from "react-icons/bs";
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from "react-icons/fa6";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import DialogContentText from '@mui/material/DialogContentText';
import { BiSolidCategory, BiSolidCoupon } from "react-icons/bi";
import DialogTitle from '@mui/material/DialogTitle';
import Modal from '@mui/material/Modal';
import { toast } from 'react-toastify';
import { FaFileAlt, FaLocationArrow, FaUser } from "react-icons/fa";
import { MdShoppingCart } from 'react-icons/md';
import { AiFillProduct } from 'react-icons/ai';

const NAVIGATION = [
  {
    kind: 'header',
    title: 'Main items',
  },
  {
    segment: 'dashboard',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    segment: 'dashboard-products',
    title: 'Products',
    icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-users',
    title: 'Users',
    icon: <HiUsers style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-categories',
    title: 'Categories',
    icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-orders',
    title: 'Orders',
    icon: <ShoppingCartIcon />,
  },
  {
    segment: 'prescriptions',
    title: 'Prescriptions',
    icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-coupons',
    title: 'Coupons',
    icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-content',
    title: 'Content',
    icon: <FaFileAlt style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-location',
    title: 'Location',
    icon: <FaLocationArrow />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Analytics',
  },
  {
    segment: 'reports',
    title: 'Reports',
    icon: <BarChartIcon />,
    children: [
      {
        segment: 'sales',
        title: 'Sales',
        icon: <DescriptionIcon />,
      },
      {
        segment: 'traffic',
        title: 'Traffic',
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    segment: 'integrations',
    title: 'Integrations',
    icon: <LayersIcon />,
  },
];

const Skeleton = styled('div')(({ theme, height }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  height,
  content: '" "',
}));

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  height: 400,
  overflowY: "scroll",
  bgcolor: "background.paper",
  border: "1px solid transparent",
  borderRadius: 1,
  shadow: 24,
  p: 4,
};

function useDemoRouter(initialPath) {
  const [pathname, setPathname] = React.useState(initialPath);
  const navigate = useNavigate();

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => {
        setPathname(String(path));
        navigate(path);
      },
    };
  }, [pathname, navigate]);

  return router;
}

const theme = createTheme({
  palette: {
    mode: 'light',
  },
});
  
function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const router = useDemoRouter('/dashboard');
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [orders, setOrders] = useState([])
  const [orderTotal, setOrderTotal] = useState()
  const [productTotal, setProductTotal] = useState()
  const [categoryTotal, setCategoryTotal] = useState()
  const [customersTotal, setCustomersTotal] = useState()

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        const response = await getAllProductsByPage(1, 10);
        setProductTotal(response.total)
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        const response = await getAllCategories(1, 10)
        setCategoryTotal(response.total)
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        const response = await getAllCustomers()
        setCustomersTotal(response.total)
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        const response = await getAllOrderData(1, 5)
        setOrders(response?.data)
        setOrderTotal(response.total)
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    setOpenLogoutDialog(false);
    onLogout();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  const branding = {
    logo: (
      <div className="header-logo">
        <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
      </div>
    ),
    title: '',
  };

  const ToolbarActions = () => (
    <Button
      variant="outlined"
      startIcon={<LogoutIcon />}
      onClick={handleLogoutClick}
      sx={{ marginRight: 2 }}
      style={{ background: "#0165AB", color: "#fff" }}
    >
      Logout
    </Button>
  );

  return (
    <AppProvider
      navigation={NAVIGATION}
      router={router}
      theme={theme}
      branding={branding}
    >
      <DashboardLayout
        slots={{
          toolbarActions: ToolbarActions,
        }}
      >
        <PageContainer>
          {loading ? (
            <Grid container spacing={1}>
              <Grid size={5} />
              <Grid size={10}>
                <Skeleton height={35} />
              </Grid>
              <Grid size={2}>
                <Skeleton height={35} />
              </Grid>
              <Grid size={10}></Grid>
              <Grid size={2}></Grid>
              {[...Array(8)].map((_, index) => (
                <Grid size={3} key={index}>
                  <Skeleton height={220} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <section className="admin-dashboard">
              <Grid container spacing={1}>
                <Grid size={3}>
                  <div style={{ position: 'relative', height: '150px', width: '100%', borderRadius: '10px', background: ' #0165ab' }}>
                    <div className="d-flex" style={{ position: 'absolute', top: '15%', left: '12%', width: '160px', justifyContent: 'space-between' }}>
                      <AiFillProduct style={{ color: '#fff', fontSize: '24px', margin: '5px 0 0' }} />
                      <h2 style={{ color: '#fff', fontSize: '22px' }}>PRODUCTS</h2>
                    </div>
                    <h1 style={{ color: '#FFF', position: 'absolute', bottom: '15%', left: '12%' }}>{productTotal}</h1>
                  </div>
                </Grid>
                <Grid size={3}>
                  <div style={{ position: 'relative', height: '150px', width: '100%', borderRadius: '10px', background: ' #0165ab' }}>
                    <div className="d-flex" style={{ position: 'absolute', top: '15%', left: '12%', width: '180px', justifyContent: 'space-between' }}>
                      <BiSolidCategory style={{ color: '#fff', fontSize: '24px', margin: '5px 0 0' }} />
                      <h2 style={{ color: '#fff', fontSize: '22px' }}>CATEGORIES</h2>
                    </div>
                    <h1 style={{ color: '#FFF', position: 'absolute', bottom: '15%', left: '12%' }}>{categoryTotal}</h1>
                  </div>
                </Grid>
                <Grid size={3}>
                  <div style={{ position: 'relative', height: '150px', width: '100%', borderRadius: '10px', background: ' #0165ab' }}>
                    <div className="d-flex" style={{ position: 'absolute', top: '15%', left: '12%', width: '175px', justifyContent: 'space-between' }}>
                      <FaUser style={{ color: '#fff', fontSize: '20px', margin: '6px 0 0' }} />
                      <h2 style={{ color: '#fff', fontSize: '22px' }}>CUSTOMERS</h2>
                    </div>
                    <h1 style={{ color: '#FFF', position: 'absolute', bottom: '15%', left: '12%' }}>{customersTotal}</h1>
                  </div>
                </Grid>
                <Grid size={3}>
                  <div style={{ position: 'relative', height: '150px', width: '100%', borderRadius: '10px', background: ' #0165ab' }}>
                    <div className="d-flex" style={{ position: 'absolute', top: '15%', left: '12%', width: '130px', justifyContent: 'space-between' }}>
                      <MdShoppingCart style={{ color: '#fff', fontSize: '24px', margin: '5px 0 0' }} />
                      <h2 style={{ color: '#fff', fontSize: '22px' }}>ORDERS</h2>
                    </div>
                    <h1 style={{ color: '#FFF', position: 'absolute', bottom: '15%', left: '12%' }}>{orderTotal}</h1>
                  </div>
                </Grid>

                <Grid size={12} height={5}></Grid>

                <Grid size={12}>
                  <table className="user-detail-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Purchase Date(M/D/Y)</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Address</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Payment Status</th>
                        <th>Prescription</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length > 0 ? (
                        orders.map((order) => {
                          const prescriptionId = order.meta_data?.find(
                            (meta) => meta.key === 'prescription_id'
                          )?.value || '';
                          const upiTransactionId = order.meta_data?.find(
                            (meta) => meta.key === 'upi_transaction_id'
                          )?.value || '';
                          return (
                            <tr key={order.id}>
                              <td>{order.id}</td>
                              <td>{new Date(order.date_created).toLocaleDateString()}</td>
                              <td>{order.billing?.first_name} {order.billing?.last_name}</td>
                              <td>{order.billing?.phone}</td>
                              <td>{order.billing?.address_1}</td>
                              <td>{order.total}</td>
                              <td>{order.payment_method_title || order.payment_method}</td>
                              <td>{order.needs_payment === true ? 'Not Paid' : 'Paid'}</td>
                              <td>
                                {prescriptionId ? (
                                  <span>Prescription ID: {prescriptionId}</span>
                                ) : upiTransactionId ? (
                                  <span>UPI Transaction ID: {upiTransactionId}</span>
                                ) : (
                                  'None'
                                )}
                              </td>
                              <td>{order.status}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="11">No orders found!</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Grid>

              </Grid>
            </section>
          )}
        </PageContainer>
      </DashboardLayout>
      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} disabled={loading}>Cancel</Button>
          <Button onClick={handleLogoutConfirm} autoFocus disabled={loading}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </AppProvider>
  );
}

export default Dashboard;